import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import { bindActionCreators } from 'redux';
import { Input } from '@netexknowledge/smart-components';
import Switch from '@material-ui/core/Switch';
import LinearProgress from '@material-ui/core/LinearProgress';

import { EDITION_MODE, MAX_LENGTH_VARIABLES } from '../../../const';
import CustomIcon from '../../common/CustomIcon';
import CustomReveal from '../../common/reveal/CustomReveal';
import * as revealActions from '../../../actions/revealActions';
import * as messageAlert from '../../../actions/messageAlertAction';
import MosaicItem from '../../common/reveal/AvatarMosaic/MosaicItem';
import PanelsBottomRowButton from '../../common/PanelsBottomRowButton';
import { getClassroomImage } from '../../../services/assets/assetsService';
import * as classroomActions from '../../../actions/classroom/classroomActions';
import ClassroomImageSelectorReveal, { CLASSROOM_IMAGE_SELECTOR_REVEAL } from './ClassroomImageSelectorReveal';

class AddClassroomReveal extends React.Component {
  constructor(context, props) {
    super(context, props);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.renderSwitch = this.renderSwitch.bind(this);
    this.closeAddModal = this.closeAddModal.bind(this);
    this.openImageSelector = this.openImageSelector.bind(this);
    this.selectClassroomImage = this.selectClassroomImage.bind(this);
    this.handlePrimaryButtonClick = this.handlePrimaryButtonClick.bind(this);
    this.mounted = true;

    this.state = {
      editionMode: EDITION_MODE.CREATION,
      defaultGroups: true,
      showDefaultGroupsHelp: false,
      classroomImageUpdated: true,
      classroomPicture: undefined,
      classroomAsset: undefined,
      isLoading: false,
      groupName: '',
      nCharacters: 0
    };
  }

  static propTypes = {
    active: PropTypes.bool,
    defaultGroupsSwitchActivated: PropTypes.bool,
    colors: PropTypes.object,
    classroomActions: PropTypes.object,
    messageAlert: PropTypes.object,
    revealActions: PropTypes.object,
    classroomImages: PropTypes.object,
    activeClassrooms: PropTypes.array,
    selectedClassroom: PropTypes.object
  };

  componentDidUpdate(prevProps, prevState) {
    const { active, selectedClassroom } = this.props;
    const { showDefaultGroupsHelp, defaultGroups, editionMode } = this.state;

    if (
      showDefaultGroupsHelp === prevState.showDefaultGroupsHelp &&
      (active !== prevProps.active || defaultGroups !== prevState.defaultGroups)
    ) {
      this.setState({
        showDefaultGroupsHelp: false
      });
    }
    if (selectedClassroom && selectedClassroom.id && editionMode !== EDITION_MODE.EDITION) {
      this.setState({
        editionMode: EDITION_MODE.EDITION,
        classroomImageUpdated: false,
        classroomAsset: selectedClassroom.asset,
        classroomPicture: parseInt(selectedClassroom.picture, 10)
      });
      this.state.groupName = selectedClassroom.name;
    }
  }

  componentDidMount() {
    const { defaultGroupsSwitchActivated } = this.props;

    this.setState({
      defaultGroups: defaultGroupsSwitchActivated
    });

    if (this.mounted) {
      this.getDefaultImage();
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handlePrimaryButtonClick = async () => {
    const { classroomActions, messageAlert, selectedClassroom } = this.props;
    const { defaultGroups, classroomImageUpdated, classroomPicture, classroomAsset, editionMode } = this.state;

    const classNameMaxLength = MAX_LENGTH_VARIABLES.CLASS_NAME;
    if (this.state.groupName.length > classNameMaxLength) {
      messageAlert.error(
        I18n.t('error.valueLength', { variable: I18n.t('home.addTitle'), length: classNameMaxLength })
      );
      return;
    }

    let classCode = this.state.groupName;
    if (classCode) {
      this.setState({
        isLoading: true
      });

      const isPicture = Boolean(classroomPicture);
      if (isPicture) {
        const picture = classroomPicture.toString();
        if (classroomImageUpdated) {
          if (EDITION_MODE.CREATION === editionMode) {
            await classroomActions.addClassroomWithPicture(classCode, defaultGroups, picture);
          } else {
            await classroomActions.editClassroomWithPicture(classCode, picture, selectedClassroom.id);
          }
        } else {
          await classroomActions.editClassroom({ name: classCode, id: selectedClassroom.id, picture });
        }
      } else {
        const asset = classroomAsset;
        if (classroomImageUpdated) {
          if (EDITION_MODE.CREATION === editionMode) {
            await classroomActions.addClassroomWithAsset(classCode, defaultGroups, asset);
          } else {
            await classroomActions.editClassroomWithAsset(classCode, asset, selectedClassroom.id);
          }
        } else {
          await classroomActions.editClassroom({ name: classCode, id: selectedClassroom.id, asset });
        }
      }
      if (this.state.groupName != null) this.state.groupName = '';
      this.closeAddModal();
    } else {
      messageAlert.error(I18n.t('classroom.emptyName'));
    }
  };

  openImageSelector() {
    const { revealActions } = this.props;
    revealActions.setOpenReveal(CLASSROOM_IMAGE_SELECTOR_REVEAL);
  }

  closeAddModal() {
    const { classroomActions } = this.props;

    classroomActions.setClassroomInfo({});
    classroomActions.setAddModalState(false);

    if (this.mounted) {
      this.setState({
        editionMode: EDITION_MODE.CREATION,
        defaultGroups: true,
        showDefaultGroupsHelp: false,
        classroomImageUpdated: false,
        classroomPicture: undefined,
        classroomAsset: undefined,
        isLoading: false
      });
    }
  }

  handleSwitch() {
    const { defaultGroups } = this.state;

    this.setState({
      defaultGroups: !defaultGroups
    });
  }

  onMouseEnter() {
    this.setState({ showDefaultGroupsHelp: true });
  }

  onMouseLeave() {
    this.setState({ showDefaultGroupsHelp: false });
  }

  renderSwitch() {
    const { defaultGroupsSwitchActivated } = this.props;
    const { defaultGroups, showDefaultGroupsHelp, isLoading } = this.state;

    if (!defaultGroupsSwitchActivated) return <div />;

    return (
      <div className="default-groups-container">
        <div className="default-groups-text">
          <span>{I18n.t('home.addClassroomPopup.createDefaultGroups')}</span>
          <div onMouseOver={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
            <CustomIcon size={16} name="help-button" />
            <div className="help-popup">
              <span className={showDefaultGroupsHelp ? 'help-popup-text show' : 'help-popup-text'}>
                {I18n.t('home.addClassroomPopup.createDefaultGroupsHelp', {
                  firstDefaultGroup: I18n.t('groups.highPerformance'),
                  secondDefaultGroup: I18n.t('groups.reinforcement')
                })}
              </span>
            </div>
          </div>
        </div>
        <div className="switch-container">
          <Switch
            data-cy="createDefaultGroupsSwitch"
            color="primary"
            disabled={isLoading}
            checked={defaultGroups}
            onChange={() => {
              this.handleSwitch();
            }}
          />
        </div>
      </div>
    );
  }

  getDefaultImage() {
    const { classroomImages, activeClassrooms } = this.props;
    const { classroomPicture, classroomAsset } = this.state;

    if (!classroomPicture && !classroomAsset && classroomImages && activeClassrooms) {
      this.setState({
        classroomPicture: (activeClassrooms.length % 10) + 1
      });
    }
  }

  selectClassroomImage(image) {
    return new Promise(resolve => {
      const isPicture = typeof image === 'number';
      this.setState(
        {
          classroomImageUpdated: true,
          classroomPicture: isPicture ? image : undefined,
          classroomAsset: isPicture ? undefined : image
        },
        () => {
          resolve();
        }
      );
    });
  }

  getClassroomImage() {
    const { classroomImages, selectedClassroom } = this.props;
    const { classroomAsset, classroomPicture } = this.state;

    return getClassroomImage(classroomImages, selectedClassroom, classroomAsset, classroomPicture);
  }

  render() {
    const { active, colors } = this.props;
    const { editionMode, classroomPicture, classroomAsset, isLoading, nCharacters } = this.state;

    const creationMode = EDITION_MODE.CREATION === editionMode;
    const title = creationMode ? 'home.newClassroom' : 'home.editClassroom';
    const primaryButtonText = creationMode ? 'common.add' : 'common.option.save';

    const classNameMaxLength = MAX_LENGTH_VARIABLES.CLASS_NAME;

    return (
      <CustomReveal
        id="add-classroom"
        active={active}
        close={this.closeAddModal}
        style={{ backgroundColor: colors.backgroundSecondary }}
      >
        <div className="add-classroom">
          <div className="header">
            <span className="close-btn" onClick={this.closeAddModal}>
              <CustomIcon name="close-button" size={24} />
            </span>
            <span className="title">{I18n.t(title)}</span>
          </div>
          <div className="container">
            <div className="addClass">
              <MosaicItem
                itemClass={'center'}
                allowImageSelectorLayer
                imageUrl={this.getClassroomImage()}
                imageSelectorLayer={this.openImageSelector}
              />
              <ClassroomImageSelectorReveal
                selectedImage={this.getClassroomImage()}
                classroomPicture={classroomPicture}
                classroomAsset={classroomAsset}
                selectClassroomImage={this.selectClassroomImage}
              />
              <div style={{ marginBottom: '24px' }}>
                <Input
                  type="text"
                  label={
                    <div className="text">
                      {I18n.t('home.addTitle')} &nbsp;&nbsp; {`${nCharacters}/${classNameMaxLength}`}
                    </div>
                  }
                  value={this.state.groupName}
                  placeholder={I18n.t('classroom.newClassCode')}
                  data-cy="contentTitle"
                  onChange={e => {
                    const inputValue = e.target.value;
                    if (inputValue.length > classNameMaxLength) {
                      e.target.value = inputValue.slice(0, classNameMaxLength);
                    } else {
                      this.setState({ groupName: inputValue, nCharacters: inputValue.length });
                    }
                  }}
                />
              </div>
              <div className="text-center">{creationMode ? this.renderSwitch() : <div />}</div>
            </div>
          </div>
          {isLoading ? <LinearProgress variant={'indeterminate'} /> : null}
          <PanelsBottomRowButton
            hideRow={false}
            disableButtons={isLoading}
            primaryButtonText={primaryButtonText}
            showCancelButton={true}
            handlePrimaryButtonClick={this.handlePrimaryButtonClick}
            handleSecondaryButtonClick={this.closeAddModal}
          />
        </div>
      </CustomReveal>
    );
  }
}

function mapStateToProps(state) {
  return {
    active: state.classrooms.addModalIsOpen,
    i18n: state.i18n,
    colors: state.theme.colors,
    classroomImages: state.theme.images.classrooms,
    activeClassrooms: state.classrooms.activeClassrooms,
    defaultGroupsSwitchActivated: state.account.configuration.defaultGroupsSwitchActivated || false,
    selectedClassroom: state.classrooms.selectedClassroom.classroom
  };
}

function mapDispatchToProps(dispatch) {
  return {
    classroomActions: bindActionCreators(classroomActions, dispatch),
    revealActions: bindActionCreators(revealActions, dispatch),
    messageAlert: bindActionCreators(messageAlert, dispatch),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddClassroomReveal);
